import { SpotBellBaseIcon } from '@tableau/icons/SpotBellBaseIcon';
import { SpotCalendarSubscriptionIcon } from '@tableau/icons/SpotCalendarSubscriptionIcon';
import { SpotCollectionBaseIcon } from '@tableau/icons/SpotCollectionBaseIcon';
import { SpotCustomViewsIcon } from '@tableau/icons/SpotCustomViewsIcon';
import { SpotMetricBaseIcon } from '@tableau/icons/SpotMetricBaseIcon';
import { SpotTrashRecycleBinIcon } from '@tableau/icons/SpotTrashRecycleBinIcon';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { CollectionDefinition } from '~src/Collections/contentDefinitions/CollectionDefinition';
import { NewCollectionButton } from '~src/Collections/space/NewCollectionButton';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { DataAlertDefinition } from '~src/dda/contentDefinitions/DataAlertDefinition';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { RecycleBinSpace } from '~src/recycleBin/space/RecycleBinSpace';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '~src/spacesPlatform/components/Explorer/FilterByTypeDropdown';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { createContentButton, explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { getDefaultContentTypeFilterValues } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { NullProjectIdForNonProjects,SiteUserSubspaceName, SpaceInfo, SpaceType, SubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { CustomViewDefinition } from '~src/views/contentDefinitions/customViews/CustomViewDefinition';

import { Messages } from '../../../../codegen/Localize';
import { dataDrivenAlertNotificationType, sendReturnFromSlackTelemetry } from '../../../../settings/IntegrationsSettings/slackConnectivity/SlackIntegrationSettingsTelemetry';
import { getMixedContentInOrder } from '../../../../spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '../../../../spacesPlatform/models/ContentType';
import { SubscriptionDefinition } from '../../../../subscription/contentDefinitions/SubscriptionDefinition';
import { IUserSettings } from '../../../../userSettings/models/IUserSettings';
import { SiteRoleOptionsResource } from '../../../services/resources/SiteRoleOptions';
import { IServerUserInfo } from '../../models/IServerUserInfo';
import { ISiteUserInfo } from '../../models/ISiteUserInfo';
import { siteUserPackagesSubspace } from './SiteUserPackagesSubspace';
import { siteUserSettingsSubspace } from './SiteUserSettingsSubspace';
import { UserSpaceHeader } from './SiteUserSpaceHeader';

export interface SiteUserSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly showWorkbooksAsViews: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'siteuser'>;
  readonly contentCountsUpToDate: boolean;
  readonly isDataAlertsEnabled: boolean;
  readonly isFlowsEnabled: boolean;
  readonly canConfigureUsers: boolean;
}

export interface SiteUserSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly fetchUpdatedContentCounts: () => void;
  readonly toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
}

export interface SiteUserSpaceOwnProps {
  readonly user: IServerUserInfo | ISiteUserInfo;
  readonly siteRole: string;
  readonly urls: SpacesSiteUrls;
  readonly userSettings: IUserSettings;
  readonly isSessionUserSelf: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSessionSiteAdmin: boolean;
  readonly isServerDomainFamilyActiveDirectory: boolean;
  readonly siteRoleOptions: SiteRoleOptionsResource;
}

export type SiteUserSpaceProps = SiteUserSpaceStateProps & SiteUserSpaceOwnProps & SiteUserSpaceDispatchProps;

export class SiteUserSpace extends React.PureComponent<SiteUserSpaceProps> {

  private memoizedGetSiteUserSubspaces = defaultMemoize(getSiteUserSubspaces);

  public componentDidUpdate(): void {
    this.fetchUpdatedCounts();
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
    this.sendTelemetryIfLinkFromSlack();
  }

  private sendTelemetryIfLinkFromSlack() {
    let sessionManagementServiceInstance = getSessionManagementServiceInstance();
    const search: any = WindowLocationService.getHashQueryObject();
    if (_.has(search, UrlParamKeys.LinkFromSlack)) {
      let slackLinkType = search[UrlParamKeys.LinkFromSlack];
      WindowLocationService.deleteQueryParameter(UrlParamKeys.LinkFromSlack);
      sendReturnFromSlackTelemetry({
        userId: sessionManagementServiceInstance.sessionUserId(),
        siteLuid: sessionManagementServiceInstance.sessionSiteLuid(),
        notificationType: dataDrivenAlertNotificationType, // TODO: If/when subscriptions onboard Slack, this will probably need to be updated
        linkType: slackLinkType
      });
    }
  }

  private static siteUrlBySubspace: Record<SiteUserSubspaceName, (urls: SpacesSiteUrls, username: string, domainName: string) => string> = {
    content: (urls, username, domainName) =>  urls.userContentUrl(username, domainName),
    subscriptions: (urls, username, domainName) =>  urls.userSubscriptionsUrl(username, domainName),
    alerts: (urls, username, domainName) =>  urls.userDataAlertsUrl(username, domainName),
    collections: (urls, username, domainName) => urls.userCollectionsUrl(username, domainName),
    customViews: (urls, username, domainName) => urls.userCustomViewsUrl(username, domainName),
    settings: (urls, username, domainName) =>  urls.userSettingsUrl(username, domainName),
    recyclebin: (urls, username, domainName) =>  urls.userRecycleBinUrl(username, domainName),
    packages: (urls, username, domainName) =>  urls.userPackagesUrl(username, domainName)
  };

  public getSiteUserUrl = (subspaceName?: SiteUserSubspaceName) => {
    let url = this.props.urls.userUrl(this.props.user.username, this.props.user.domainName);
    if (subspaceName) {
      const urlFn = SiteUserSpace.siteUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls, this.props.user.username, this.props.user.domainName) || url;
    }

    return url;
  };

  public render() {
    const subspaces = this.memoizedGetSiteUserSubspaces(
      this.props.contentCounts,
      this.props.contentCountsUpToDate,
      this.props.isSessionServerAdmin || this.props.isSessionSiteAdmin || this.props.isSessionUserSelf,
      this.props.isSessionUserSelf,
      this.props.isDataAlertsEnabled,
      this.props.userSettings,
      this.props.user as ISiteUserInfo,
      this.props.urls,
      this.props.contentTypeFilterValues,
      this.props.defaultContentTypeFilterValues,
      this.props.showWorkbooksAsViews,
      this.props.filterSelected,
      this.props.toggleShowWorkbooksAsViews,
      this.props.clearContentCounts,
      this.props.spaceInfo.subspaceName,
      this.props.isFlowsEnabled,
      this.props.canConfigureUsers
    );

    const rawSubspaceName = this.props.spaceInfo.subspaceName;
    const subspaceTabName: SubspaceName = rawSubspaceName;

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={subspaceTabName}
        urlForSubspace={this.getSiteUserUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'siteuser'}
        spaceItemName={this.props.user.displayName}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    return (
      <UserSpaceHeader
        item={this.props.user}
        inServerArea={false}
        siteRole={this.props.siteRole}
        siteRoleOptions={this.props.siteRoleOptions}
        userSettings={this.props.userSettings}
        isSessionUserSelf={this.props.isSessionUserSelf}
        isSessionServerAdmin={this.props.isSessionServerAdmin}
        isSessionSiteAdmin={this.props.isSessionSiteAdmin}
        isServerDomainFamilyActiveDirectory={this.props.isServerDomainFamilyActiveDirectory}
      />
    );
  }

  private fetchUpdatedCounts = (): void => {
    this.props.fetchUpdatedContentCounts();
  };
}

function contentFilterControl(
  contentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  showWorkbooksAsViews: boolean,
  filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void,
  toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void,
  clearContentCounts: (spaceType: SpaceType) => void
): JSX.Element {
  return (
    <React.Fragment>
      <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
         {Messages.filterByTypeDropdown_label()}
      </span>
      <FilterByTypeDropdown
        filterSelected={filterSelected}
        contentFilter={contentTypeFilterValues}
        defaultContentTypeFilterValues={defaultContentTypeFilterValues}
        showWorkbooksAsViews={showWorkbooksAsViews}
        toggleShowWorkbooksAsViews={toggleShowWorkbooksAsViews}
        clearContentCounts={clearContentCounts}
        spaceType={'siteuser'}
        contentItemDefinitions={getMixedContentInOrder()}
      />
    </React.Fragment>
  );
}


function getSiteUserContentSubspaceCount(currentSubspace: SubspaceName, isFlowsEnabled: boolean, contentCounts: ISpaceInfoTypeItemCount<'siteuser'>, contentTypeFilterValues: ReadonlyArray<ExplorableItemType>) {
  let contentTypes = contentTypeFilterValues;

  if(currentSubspace !== 'content') {
    contentTypes = getDefaultContentTypeFilterValues({item: null, spaceType: 'siteuser', subspaceName: 'content'}, isFlowsEnabled);
  }

  return contentCounts && (
            (_.contains(contentTypes, 'project') ? contentCounts['project'] : 0) +
            (_.contains(contentTypes, 'workbook') ? contentCounts['workbook'] : 0) +
            (_.contains(contentTypes, 'view') ? contentCounts['view'] : 0) +
            (_.contains(contentTypes, 'virtualConnection') ? contentCounts['virtualConnection'] : 0) +
            (_.contains(contentTypes, 'datasource') ? contentCounts['datasource'] : 0) +
            (_.contains(contentTypes, 'flow') ? contentCounts['flow'] : 0)
  );
}

function getSiteUserSubspaces(
  contentCounts: ISpaceInfoTypeItemCount<'siteuser'>,
  contentCountsUpToDate: boolean,
  isSelfOrAdmin: boolean,
  isSessionUserSelf: boolean,
  isDataAlertsEnabled: boolean,
  userSettings: IUserSettings,
  siteUser: ISiteUserInfo,
  urls: SpacesSiteUrls,
  contentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  showWorkbooksAsViews: boolean,
  filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void,
  toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void,
  clearContentCounts: (spaceType: SpaceType) => void,
  subspaceName: SubspaceName,
  isFlowsEnabled: boolean,
  canConfigureUsers: boolean
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    explorerSubspace({
      name: 'content',
      tabLabel: Messages.content(),
      supportedDisplayModes: ProjectDefinition.supportedDisplayModes,
      supportFilterPanel: true,
      contentCount: getSiteUserContentSubspaceCount(subspaceName, isFlowsEnabled, contentCounts, contentTypeFilterValues),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        icon: SpotMetricBaseIcon,
        primaryMessage: Messages.contentEmptyMessage()
      },
      toolbarContentFilterControl: contentFilterControl(
        contentTypeFilterValues,
        defaultContentTypeFilterValues,
        showWorkbooksAsViews,
        filterSelected,
        toggleShowWorkbooksAsViews,
        clearContentCounts
      ),
      toolbarContentSpecificControls: {
        beginning: createContentButton({ projectId: NullProjectIdForNonProjects, spaceType: 'siteuser' })
      },
    }),
    isSelfOrAdmin && isDataAlertsEnabled && explorerSubspace({
      name: 'alerts',
      tabLabel: Messages.dataAlerts(),
      supportedDisplayModes: DataAlertDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      contentCount: contentCounts && contentCounts['alerts'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      ...(isSessionUserSelf && {emptyStateProps: {
        icon: SpotBellBaseIcon,
        primaryMessage: Messages.tasksEmptyAlertsSelf()
      }}),
    }),
    isSelfOrAdmin && explorerSubspace({
      name: 'customViews',
      tabLabel: Messages.customViews(),
      supportedDisplayModes: CustomViewDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      contentCount: contentCounts && contentCounts['customViews'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      ...(isSessionUserSelf && {emptyStateProps: {
        icon: SpotCustomViewsIcon,
        primaryMessage: Messages.usersCustomViewsEmptyMessage()
      }}),
    }),
    isSelfOrAdmin && explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      supportFilterPanel: true,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      ...(isSessionUserSelf && {emptyStateProps: {
        icon: SpotCalendarSubscriptionIcon,
        primaryMessage: Messages.tasksEmptySubscriptionsSelf()
      }}),
    }),
    CollectionUtils.checkCollectionSiteSetting() && explorerSubspace({
      name: 'collections',
      tabLabel: Messages.collections(),
      supportedDisplayModes: CollectionDefinition.supportedDisplayModes,
      supportFilterPanel: true,
      contentCount: contentCounts && contentCounts['collections'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        icon: SpotCollectionBaseIcon,
        useContentTypeEmptyStateMessage: true,
        primaryMessage: Messages.contentEmptyMessage()
      },
      toolbarContentSpecificControls: {
        beginning: createNewCollectionButton(urls, siteUser.displayName)
      },
    }),
    isSelfOrAdmin && RecycleBinUtils.isRecycleBinEnabled() && explorerSubspace({
      name: 'recyclebin',
      tabLabel: Messages.recycleBinBrowserTitle(),
      supportedDisplayModes: ProjectDefinition.supportedDisplayModes,
      supportFilterPanel: true,
      shouldRenderContentCount: false,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        icon: SpotTrashRecycleBinIcon,
        useContentTypeEmptyStateMessage: true,
        primaryMessage: Messages.contentEmptyMessage()
      },
      toolbarContentFilterControl: RecycleBinSpace.contentFilterControlForSiteUserSpace(
        contentTypeFilterValues,
        defaultContentTypeFilterValues,
        filterSelected
      ),
    }),
    (isSelfOrAdmin || canConfigureUsers) && !!userSettings && siteUserSettingsSubspace({
      isSessionUserSelf,
      userSettings,
      siteUser,
      urls
    }),
    FeatureFlags.packagesEnabled && isSelfOrAdmin && siteUserPackagesSubspace({
      username: siteUser.username,
      domainName: siteUser.domainName ?? '',
      urls
    })
  ]);
}

function createNewCollectionButton(urls: SpacesSiteUrls, userDisplayName: string): JSX.Element {
  return (<NewCollectionButton
    urls={urls}
    userDisplayName={userDisplayName} />);
}
